import { Link, useNavigate, useParams } from "react-router-dom";
import { SideBar } from "./inc/sideBar";
import { TopHeader } from "./inc/topHeader";
import { useEffect, useState, useCallback, useRef } from "react";
import { ToastContainer } from "react-toastify";
import {
  getProgressById,
  getProgressFile,
} from "../../services/ProgressService";
import { getCommentsByProgressId } from "../../services/commentService";
import { getCoordinatorByEmail } from "../../services/CoordinatorService";
import { getStudyById } from "../../services/StudyService";

const ViewProgressA = () => {
  const { id } = useParams();
  const navigator = useNavigate();
  const [progress, setProgress] = useState(null);
  const [study, setStudy] = useState(null);
  const [comments, setComments] = useState([]);
  const [coordinatorNames, setCoordinatorNames] = useState({});

  const [loading, setLoading] = useState(true);
  const [commentLoading, setCommentLoading] = useState(true);
  const [error, setError] = useState(null);

  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [activeMenu, setActiveMenu] = useState(null);
  const [windowWidth, setWindowWidth] = useState(
    typeof window !== "undefined" ? window.innerWidth : 1024
  );

  const pendingRequests = useRef(new Set());

  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (windowWidth < 768) {
      setSidebarOpen(false);
    }
  }, [windowWidth]);

  const toggleMenu = (menu) => {
    setActiveMenu(activeMenu === menu ? null : menu);
  };

  const fetchCoordinatorName = useCallback(
    async (email) => {
      if (!email || email === "N/A" || email === "undefined") {
        return email || "N/A";
      }

      if (coordinatorNames[email]) {
        return coordinatorNames[email];
      }

      if (pendingRequests.current.has(email)) {
        return null;
      }

      pendingRequests.current.add(email);

      try {
        const response = await getCoordinatorByEmail(email);
        const fullName = `${response.data.fullName}`;

        setCoordinatorNames((prev) => ({ ...prev, [email]: fullName }));
        pendingRequests.current.delete(email);
        return fullName;
      } catch (err) {
        console.error(`Failed to fetch coordinator name for ${email}:`, err);
        setCoordinatorNames((prev) => ({ ...prev, [email]: email }));
        pendingRequests.current.delete(email);
        return email;
      }
    },
    [coordinatorNames]
  );

  const fetchComments = useCallback(
    async (progressId) => {
      setCommentLoading(true);
      try {
        const response = await getCommentsByProgressId(progressId);

        const sortedComments = response.data.sort(
          (a, b) => new Date(b.commentedDate) - new Date(a.commentedDate)
        );
        setComments(sortedComments);

        const uniqueEmails = [
          ...new Set(
            sortedComments
              .map((c) => c.coordinatorEmail)
              .filter((email) => email && email !== "undefined")
          ),
        ];

        for (const email of uniqueEmails) {
          if (!coordinatorNames[email]) {
            await fetchCoordinatorName(email);
          }
        }

        setCommentLoading(false);
      } catch (err) {
        console.error("Error fetching comments:", err);
        setCommentLoading(false);
      }
    },
    [coordinatorNames, fetchCoordinatorName]
  );

  const getProgressStatus = useCallback(() => {
    if (!progress || !study)
      return { status: "N/A", badgeClass: "bg-secondary" };

    const criteriaMet = [];
    const finishDate = new Date(study.finish_date);

    const meetProgressPlan = progress.meet_progress_plan;
    if (meetProgressPlan && meetProgressPlan.toUpperCase() === "YES") {
      criteriaMet.push(true);
    } else {
      criteriaMet.push(false);
    }

    if (progress.submit_thesis) {
      const submitThesisDate = new Date(progress.submit_thesis);
      if (submitThesisDate < finishDate) {
        criteriaMet.push(true);
      } else {
        criteriaMet.push(false);
      }
    } else {
      criteriaMet.push(false);
    }

    if (progress.study_complition) {
      const studyComplitionDate = new Date(progress.study_complition);
      if (studyComplitionDate <= finishDate) {
        criteriaMet.push(true);
      } else {
        criteriaMet.push(false);
      }
    } else {
      criteriaMet.push(false);
    }

    const metCount = criteriaMet.filter(Boolean).length;

    if (metCount === 3) {
      return { status: "Good progress", badgeClass: "bg-success" };
    } else if (metCount === 2) {
      return {
        status: "Moderate progress",
        badgeClass: "bg-warning text-dark",
      };
    } else {
      return { status: "Poor progress", badgeClass: "bg-danger" };
    }
  }, [progress, study]);

  useEffect(() => {
    const fetchData = async () => {
      if (!id) {
        setError("Progress ID is missing from the URL.");
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const progressResponse = await getProgressById(id);
        const fetchedProgress = progressResponse.data;
        setProgress(fetchedProgress);

        if (fetchedProgress.studyId && fetchedProgress.studyId.studyId) {
          const studyId = fetchedProgress.studyId.studyId;
          const studyResponse = await getStudyById(studyId);
          setStudy(studyResponse.data);
        } else if (fetchedProgress.studyId) {
          setStudy(fetchedProgress.studyId);
        }

        setLoading(false);
        fetchComments(id);
      } catch (err) {
        console.error("Error fetching details:", err);
        setError("Failed to fetch progress or study details.");
        setLoading(false);
      }
    };

    fetchData();
  }, [id, fetchComments]);

  const handleViewReport = (documentPath) => {
    if (documentPath) {
      const fileUrl = getProgressFile(documentPath);
      window.open(fileUrl, "_blank");
    }
  };

  const renderProgressDetails = () => {
    if (!progress) {
      return (
        <tr>
          <td colSpan="2">No data found for this progress ID.</td>
        </tr>
      );
    }

    const progressStatus = getProgressStatus();

    const fields = {
      "Previous progress": progress.previous,
      "Current progress": progress.currently,
      "Next month progress": progress.next,
      "Study completion date": progress.study_complition,
      Challenges: progress.challenges,
      Suggestion: progress.suggestion,
      "Do you think your progress is going as you planned":
        progress.meet_progress_plan,
      "When do you expect to submit Thesis / Dissertation":
        progress.submit_thesis,
      "When did you meet or communicate with your supervisor":
        progress.meet_supervisor,
      "When did you receive last response from your supervisor":
        progress.supervisor_response,
      "Submitted Date": progress.progress_date,
    };

    return (
      <>
        {Object.entries(fields).map(([label, value], index) => (
          <tr key={index}>
            <th style={{ width: "30%" }}>{label}</th>
            <td>
              {value !== null && value !== undefined && value !== ""
                ? value.toString()
                : "N/A"}
            </td>
          </tr>
        ))}

        <tr>
          <th style={{ width: "30%" }}>Evaluation Status</th>
          <td>
            <span className={`badge fw-bold fs-6 ${progressStatus.badgeClass}`}>
              {progressStatus.status}
            </span>
          </td>
        </tr>

        <tr>
          <th style={{ width: "30%" }}>Progress Status</th>
          <td>
            {(() => {
              let badgeClass = "bg-primary";
              const status = progress.progress_status?.toLowerCase();

              if (status === "pending") badgeClass = "bg-secondary text-white";
              else if (status === "rejected")
                badgeClass = "bg-danger text-white";
              else if (status === "on progress")
                badgeClass = "bg-warning text-dark";
              else if (status === "accepted")
                badgeClass = "bg-success text-white";

              return (
                <span className={`badge fw-bold fs-6 ${badgeClass}`}>
                  {progress.progress_status || "N/A"}
                </span>
              );
            })()}
          </td>
        </tr>

        <tr>
          <th style={{ width: "30%" }}>View signed report</th>
          <td>
            {progress.document_path ? (
              <button
                className="btn btn-sm btn-black rounded-2"
                onClick={() => handleViewReport(progress.document_path)}
              >
                <i className="fa fa-file-pdf me-2 fs-5"></i> View Report
              </button>
            ) : (
              "No document uploaded"
            )}
          </td>
        </tr>
      </>
    );
  };

  const renderCommentsTable = () => {
    if (commentLoading) {
      return <div className="text-center p-3">Loading comments...</div>;
    }

    if (comments.length === 0) {
      return <div className="text-center p-3">No comments yet.</div>;
    }

    return (
      <div className="table-responsive">
        <table className="table table-striped table-bordered table-hover">
          <thead>
            <tr>
              <th>S/N</th>
              <th>Comment</th>
              <th>Coordinator</th>
              <th style={{ width: "15%" }}>Date</th>
            </tr>
          </thead>
          <tbody>
            {comments.map((comment, index) => (
              <tr key={comment.commentId}>
                <td>{index + 1}</td>
                <td style={{ whiteSpace: "pre-wrap" }}>{comment.comment}</td>
                <td>
                  {coordinatorNames[comment.coordinator_email]
                    ? coordinatorNames[comment.coordinator_email]
                    : comment.coordinator_email || "N/A"}
                </td>
                <td>
                  {comment.commented_date
                    ? new Date(comment.commented_date).toLocaleDateString()
                    : "N/A"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <>
      <ToastContainer />
      <div className="d-flex position-relative" style={{ minHeight: "100vh" }}>
        <SideBar
          sidebarOpen={sidebarOpen}
          setSidebarOpen={setSidebarOpen}
          activeMenu={activeMenu}
          toggleMenu={toggleMenu}
          windowWidth={windowWidth}
        />
        <div className="main-panel">
          <TopHeader
            sidebarOpen={sidebarOpen}
            setSidebarOpen={setSidebarOpen}
            windowWidth={windowWidth}
          />
          <div className="container mt-0">
            <div className="page-inner">
              <div className="page-header bg-white zis-shadow rounded-2 mt-2">
                <ul className="breadcrumbs my-md-3 mb-3 ms-0 px-4">
                  <li className="nav-home">
                    <Link to="/admin">
                      <span className="fa fa-home"></span>
                    </Link>
                  </li>
                  <li className="separator">
                    <i className="fas fa-chevron-right text-secondary"></i>
                  </li>
                  <li className="nav-item text-black fw-semibold">
                    <Link to="/admin/student">Manage student</Link>
                  </li>
                  <li className="separator">
                    <i className="fas fa-chevron-right text-secondary"></i>
                  </li>
                  <li className="nav-item text-black fw-semibold">
                    <button
                      className="btn btn-link p-0 text-decoration-none text-muted"
                      onClick={() => navigator(-2)}
                    >
                      Student informations
                    </button>
                  </li>
                  <li className="separator">
                    <i className="fas fa-chevron-right text-secondary"></i>
                  </li>
                  <li className="nav-item text-black fw-semibold">
                    <button
                      className="btn btn-link text-decoration-none text-muted"
                      onClick={() => navigator(-1)}
                    >
                      Manage progress
                    </button>
                  </li>
                  <li className="separator">
                    <i className="fas fa-chevron-right text-secondary"></i>
                  </li>
                  <li className="nav-item text-black fw-semibold">
                    <Link to="#">View progress details</Link>
                  </li>
                </ul>
              </div>

              <div className="row">
                <div className="col-12">
                  <div className="card zis-shadow rounded-0">
                    <div className="card-header">
                      <div className="card-title">
                        <h3 className="mt-2">View progress details</h3>
                      </div>
                    </div>
                    <div className="card-body">
                      {loading && (
                        <div className="text-center">
                          Loading progress and study details...
                        </div>
                      )}
                      {error && (
                        <div className="alert alert-danger text-center">
                          {error}
                        </div>
                      )}
                      {!loading && !error && (
                        <div className="table-responsive">
                          <table className="table table-bordered table-striped">
                            <tbody>{renderProgressDetails()}</tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="col-12 mt-4">
                  <div className="card zis-shadow rounded-0">
                    <div className="card-header">
                      <div className="card-title">
                        <h4 className="mt-2">Comment History</h4>
                      </div>
                    </div>
                    <div className="card-body">{renderCommentsTable()}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ViewProgressA;
